import { useState, useEffect, useCallback } from 'react';
import { getFeedback, updateFeedbackStatus } from '../api/client';

interface FeedbackItem {
  id: string;
  type: string;
  message: string;
  user_id?: string;
  anonymous_id?: string;
  pin_sku?: string;
  status: string;
  created_at: string;
  updated_at: string;
}

export default function Feedback() {
  const [filter, setFilter] = useState<string>('all');
  const [feedbackItems, setFeedbackItems] = useState<FeedbackItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [updating, setUpdating] = useState<string | null>(null);

  const fetchFeedback = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const statusFilter = ['pending', 'reviewed', 'resolved'].includes(filter) ? filter : undefined;
      const typeFilter = ['bug', 'feature', 'general'].includes(filter) ? filter : undefined;
      const result = await getFeedback(page, 20, statusFilter, typeFilter);
      setFeedbackItems(result.data?.feedback || []);
      setTotalPages(result.data?.pagination?.totalPages || 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load feedback');
    } finally {
      setLoading(false);
    }
  }, [page, filter]);

  useEffect(() => {
    fetchFeedback();
  }, [fetchFeedback]);

  const handleStatusUpdate = async (id: string, newStatus: string) => {
    setUpdating(id);
    try {
      await updateFeedbackStatus(id, newStatus);
      fetchFeedback();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update status');
    } finally {
      setUpdating(null);
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'bug': return '&#x1F41B;';
      case 'feature': return '&#x2728;';
      default: return '&#x1F4AC;';
    }
  };

  const getTypeBadgeClasses = (type: string) => {
    switch (type) {
      case 'bug': return 'bg-red-100 text-red-700';
      case 'feature': return 'bg-purple-100 text-purple-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusBadgeClasses = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-blue-100 text-blue-700';
      case 'reviewed': return 'bg-yellow-100 text-yellow-700';
      case 'resolved': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${Math.floor(diffHours / 24)}d ago`;
  };

  const pendingCount = feedbackItems.filter(i => i.status === 'pending').length;
  const bugCount = feedbackItems.filter(i => i.type === 'bug').length;
  const featureCount = feedbackItems.filter(i => i.type === 'feature').length;

  return (
    <div>
      {/* Page Header */}
      <div className="flex justify-between items-start mb-8 pb-5 border-b-2 border-gray-200">
        <div>
          <h2 className="text-3xl font-bold text-slate-800 mb-1">User Feedback</h2>
          <div className="text-gray-500">{feedbackItems.length} feedback submissions</div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchFeedback}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors text-sm"
          >
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 text-red-500 p-4 rounded-lg mb-6">
          {error}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-4 gap-5 mb-6">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-slate-800">{feedbackItems.length}</div>
          <div className="text-sm text-gray-500">Total Feedback</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-blue-600">{pendingCount}</div>
          <div className="text-sm text-gray-500">Pending</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-red-600">{bugCount}</div>
          <div className="text-sm text-gray-500">Bug Reports</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-purple-600">{featureCount}</div>
          <div className="text-sm text-gray-500">Feature Requests</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex gap-2">
          {['all', 'pending', 'bug', 'feature', 'resolved'].map((f) => (
            <button
              key={f}
              onClick={() => { setFilter(f); setPage(1); }}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                filter === f
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Feedback List */}
      {loading ? (
        <div className="flex items-center justify-center p-12 text-gray-500">
          Loading feedback...
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {feedbackItems.length === 0 ? (
            <div className="p-12 text-center">
              <div className="text-4xl mb-4">&#x1F4ED;</div>
              <div className="text-xl font-medium text-slate-800 mb-2">No Feedback</div>
              <div className="text-gray-500">No feedback items match your filter</div>
            </div>
          ) : (
            feedbackItems.map((item, index) => (
              <div
                key={item.id}
                className={`p-4 ${index !== feedbackItems.length - 1 ? 'border-b border-gray-200' : ''} ${item.status === 'pending' ? 'bg-blue-50' : ''}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-xl flex-shrink-0" dangerouslySetInnerHTML={{ __html: getTypeIcon(item.type) }} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`inline-flex items-center px-2 py-0.5 text-xs font-bold rounded-full ${getTypeBadgeClasses(item.type)}`}>
                        {item.type}
                      </span>
                      <span className={`inline-flex items-center px-2 py-0.5 text-xs font-bold rounded-full ${getStatusBadgeClasses(item.status)}`}>
                        {item.status}
                      </span>
                      {item.pin_sku && (
                        <span className="text-xs text-gray-500">SKU: {item.pin_sku}</span>
                      )}
                      <span className="text-xs text-gray-400 ml-auto">{formatTime(item.created_at)}</span>
                    </div>
                    <p className="text-slate-800 mb-2">{item.message}</p>
                    <div className="text-xs text-gray-500">
                      {item.user_id ? `User ID: ${item.user_id.substring(0, 8)}...` : item.anonymous_id ? `Anonymous: ${item.anonymous_id.substring(0, 8)}...` : 'Anonymous'}
                    </div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    {item.status === 'pending' && (
                      <button
                        onClick={() => handleStatusUpdate(item.id, 'reviewed')}
                        disabled={updating === item.id}
                        className="px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-100 rounded hover:bg-blue-200 transition-colors disabled:opacity-50"
                      >
                        {updating === item.id ? '...' : 'Mark Reviewed'}
                      </button>
                    )}
                    {item.status !== 'resolved' && (
                      <button
                        onClick={() => handleStatusUpdate(item.id, 'resolved')}
                        disabled={updating === item.id}
                        className="px-3 py-1.5 text-xs font-medium text-green-600 bg-green-100 rounded hover:bg-green-200 transition-colors disabled:opacity-50"
                      >
                        {updating === item.id ? '...' : 'Resolve'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-4 mt-6">
          <button
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={page === 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Previous
          </button>
          <span className="text-gray-600">
            Page {page} of {totalPages}
          </span>
          <button
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={page === totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
